import type { ModuleKey } from '@cloud/modules'
import type { NavigationType } from '@/types'

/**
 * Navigation items for ERP modules
 * These are conditionally rendered based on module enablement
 */
export const moduleNavigationsData: NavigationType[] = [
  {
    title: 'Personal',
    items: [
      {
        title: 'My HR',
        href: '/hr/me',
        iconName: 'CircleUser',
      },
    ],
  },
  {
    title: 'Settings',
    items: [
      {
        title: 'Modules',
        href: '/settings/modules',
        iconName: 'Package',
      },
      {
        title: 'Custom Modules',
        href: '/settings/custom-modules',
        iconName: 'Puzzle',
      },
      {
        title: 'Marketplace',
        href: '/settings/marketplace',
        iconName: 'Store',
      },
      {
        title: 'Quotas',
        href: '/settings/quotas',
        iconName: 'Gauge',
      },
      {
        title: 'Roles & Permissions',
        href: '/settings/roles',
        iconName: 'Shield',
      },
      {
        title: 'Teams',
        href: '/settings/teams',
        iconName: 'Users',
      },
      {
        title: 'API Keys',
        href: '/settings/api-keys',
        iconName: 'Key',
      },
    ],
  },
  {
    title: 'CRM',
    items: [
      {
        title: 'Dashboard',
        href: '/crm',
        iconName: 'LayoutDashboard',
        moduleKey: 'crm',
      },
      {
        title: 'Leads',
        href: '/crm/leads',
        iconName: 'UserPlus',
        moduleKey: 'crm',
      },
      {
        title: 'Accounts',
        href: '/crm/accounts',
        iconName: 'Building2',
        moduleKey: 'crm',
      },
      {
        title: 'Contacts',
        href: '/crm/contacts',
        iconName: 'User',
        moduleKey: 'crm',
      },
      {
        title: 'Opportunities',
        href: '/crm/opportunities',
        iconName: 'Target',
        moduleKey: 'crm',
      },
      {
        title: 'Activities',
        href: '/crm/activities',
        iconName: 'CalendarCheck',
        moduleKey: 'crm',
      },
      {
        title: 'CRM Settings',
        iconName: 'Settings',
        moduleKey: 'crm',
        items: [
          {
            title: 'Pipelines',
            href: '/settings/crm/pipelines',
          },
          {
            title: 'Webhooks',
            href: '/settings/crm/webhooks',
          },
          {
            title: 'Currency Settings',
            href: '/settings/crm/currency-settings',
          },
          {
            title: 'Currency Rates',
            href: '/settings/crm/currency-rates',
          },
          {
            title: 'Lookups',
            href: '/settings/crm/lookups',
          },
        ],
      },
    ],
  },
  {
    title: 'Human Resources',
    items: [
      {
        title: 'Dashboard',
        href: '/hr',
        iconName: 'LayoutDashboard',
        moduleKey: 'hr',
      },
      {
        title: 'Employees',
        href: '/hr/employees',
        iconName: 'Users',
        moduleKey: 'hr',
      },
      {
        title: 'Departments',
        href: '/hr/departments',
        iconName: 'Building2',
        moduleKey: 'hr',
      },
      {
        title: 'Attendance',
        href: '/hr/attendance',
        iconName: 'CalendarCheck',
        moduleKey: 'hr',
      },
      {
        title: 'Shifts',
        href: '/hr/shifts',
        iconName: 'Clock',
        moduleKey: 'hr',
      },
      {
        title: 'Leave',
        href: '/hr/leave',
        iconName: 'CalendarOff',
        moduleKey: 'hr',
      },
      {
        title: 'Onboarding',
        href: '/hr/onboarding',
        iconName: 'UserCheck',
        moduleKey: 'hr',
      },
      {
        title: 'Contracts',
        href: '/hr/contracts',
        iconName: 'FileText',
        moduleKey: 'hr',
      },
      {
        title: 'Documents',
        href: '/hr/documents',
        iconName: 'FolderOpen',
        moduleKey: 'hr',
      },
      {
        title: 'Payroll',
        href: '/hr/payroll',
        iconName: 'Banknote',
        moduleKey: 'hr',
      },
      {
        title: 'Expenses',
        href: '/hr/expense-claims',
        iconName: 'Receipt',
        moduleKey: 'hr',
      },
      {
        title: 'Recruitment',
        href: '/hr/recruitment',
        iconName: 'UserPlus',
        moduleKey: 'hr',
      },
      {
        title: 'Training',
        href: '/hr/training',
        iconName: 'GraduationCap',
        moduleKey: 'hr',
      },
      {
        title: 'Performance',
        href: '/hr/performance-reviews',
        iconName: 'TrendingUp',
        moduleKey: 'hr',
      },
      {
        title: 'Lifecycle',
        href: '/hr/lifecycle',
        iconName: 'GitBranch',
        moduleKey: 'hr',
      },
    ],
  },
  {
    title: 'Finance',
    items: [
      {
        title: 'Dashboard',
        href: '/finance/dashboard',
        iconName: 'Wallet',
        moduleKey: 'finance',
      },
      {
        title: 'Invoices',
        href: '/finance/invoices',
        iconName: 'FileText',
        moduleKey: 'finance',
      },
      {
        title: 'Expenses',
        href: '/finance/expenses',
        iconName: 'Receipt',
        moduleKey: 'finance',
      },
      {
        title: 'Accounts',
        href: '/finance/accounts',
        iconName: 'CreditCard',
        moduleKey: 'finance',
      },
      {
        title: 'Reports',
        href: '/finance/reports',
        iconName: 'ChartBar',
        moduleKey: 'finance',
      },
    ],
  },
  {
    title: 'Inventory',
    items: [
      {
        title: 'Products',
        href: '/inventory/products',
        iconName: 'Package',
        moduleKey: 'inventory',
      },
      {
        title: 'Stock',
        href: '/inventory/stock',
        iconName: 'Boxes',
        moduleKey: 'inventory',
      },
      {
        title: 'Warehouse',
        href: '/inventory/warehouse',
        iconName: 'Warehouse',
        moduleKey: 'inventory',
      },
      {
        title: 'Suppliers',
        href: '/inventory/suppliers',
        iconName: 'Truck',
        moduleKey: 'inventory',
      },
      {
        title: 'Transfer',
        href: '/inventory/transfer',
        iconName: 'ArrowLeftRight',
        moduleKey: 'inventory',
      },
    ],
  },
  {
    title: 'Projects',
    items: [
      {
        title: 'All Projects',
        href: '/projects',
        iconName: 'FolderKanban',
        moduleKey: 'projects',
      },
      {
        title: 'Tasks',
        href: '/projects/tasks',
        iconName: 'SquareCheck',
        moduleKey: 'projects',
      },
      {
        title: 'Time Tracking',
        href: '/projects/time',
        iconName: 'Timer',
        moduleKey: 'projects',
      },
      {
        title: 'Milestones',
        href: '/projects/milestones',
        iconName: 'Flag',
        moduleKey: 'projects',
      },
      {
        title: 'Team',
        href: '/projects/team',
        iconName: 'Users',
        moduleKey: 'projects',
      },
    ],
  },
]

/**
 * Maps navigation item titles to their corresponding module keys
 * Used to determine which module a nav item belongs to
 */
export const navItemModuleMap: Record<string, ModuleKey> = {
  // HR
  Employees: 'hr',
  Attendance: 'hr',
  Leave: 'hr',
  Recruitment: 'hr',
  Payroll: 'hr',

  // Finance
  Dashboard: 'finance',
  Invoices: 'finance',
  Expenses: 'finance',
  Accounts: 'finance',
  Reports: 'finance',

  // Inventory
  Products: 'inventory',
  Stock: 'inventory',
  Warehouse: 'inventory',
  Suppliers: 'inventory',
  Transfer: 'inventory',

  // CRM
  Contacts: 'crm',
  Companies: 'crm',
  Deals: 'crm',
  Pipeline: 'crm',
  Calls: 'crm',
  Meetings: 'crm',
  Tasks: 'crm',

  // Projects
  'All Projects': 'projects',
  'Time Tracking': 'projects',
  Milestones: 'projects',
  Team: 'projects',
}
